import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Check, Loader as Loader2, Mail, Send, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

interface SmtpSettings {
  id: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  from_email: string;
  from_name: string;
}

const defaultSettings: Omit<SmtpSettings, 'id'> = {
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  from_email: '',
  from_name: '',
};

type TestStatus = 'idle' | 'sending' | 'success' | 'error';

interface TestResult {
  status: TestStatus;
  message: string;
  detail?: string;
}

export function SmtpTab() {
  const [settings, setSettings] = useState<Omit<SmtpSettings, 'id'>>(defaultSettings);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<TestResult>({ status: 'idle', message: '' });
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase.from('smtp_settings').select('*').maybeSingle();
      if (data) {
        setExistingId(data.id);
        setSettings({
          host: data.host ?? '',
          port: data.port ?? 587,
          secure: data.secure ?? false,
          username: data.username ?? '',
          password: data.password ?? '',
          from_email: data.from_email ?? '',
          from_name: data.from_name ?? '',
        });
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    const payload = {
      host: settings.host.trim(),
      port: settings.port,
      secure: settings.secure,
      username: settings.username.trim(),
      password: settings.password,
      from_email: settings.from_email.trim(),
      from_name: settings.from_name.trim(),
      updated_at: new Date().toISOString(),
    };

    let err;
    if (existingId) {
      ({ error: err } = await supabase.from('smtp_settings').update(payload).eq('id', existingId));
    } else {
      const { data, error: insertErr } = await supabase.from('smtp_settings').insert(payload).select('id').maybeSingle();
      err = insertErr;
      if (data) setExistingId(data.id);
    }

    setSaving(false);
    if (err) { setError(err.message); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleTestEmail = async () => {
    const recipient = testTo.trim() || settings.from_email.trim();
    if (!recipient) {
      setTestResult({ status: 'error', message: 'Enter a recipient email address to send the test to.' });
      return;
    }

    setTestResult({ status: 'sending', message: 'Sending test email...' });
    setShowDetail(false);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-quotation-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          _test: true,
          testTo: recipient,
        }),
      });

      const json = await res.json();

      if (!res.ok || json.error) {
        setTestResult({
          status: 'error',
          message: 'Email delivery failed.',
          detail: json.error ?? `HTTP ${res.status}`,
        });
      } else {
        setTestResult({
          status: 'success',
          message: `Test email sent to ${recipient}. Check your inbox.`,
          detail: json.detail ?? undefined,
        });
      }
    } catch (e) {
      setTestResult({
        status: 'error',
        message: 'Could not reach the email function.',
        detail: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const inputClass = "w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">SMTP Settings</h2>
          <p className="text-xs text-slate-500 mt-0.5">Configure outgoing email for quotation notifications</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
      )}
      {saved && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 rounded-lg flex items-center gap-2">
          <Check className="h-4 w-4" />
          SMTP settings saved successfully.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-5">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
          <Mail className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Server Configuration</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs text-slate-600">SMTP Host</Label>
            <input
              type="text"
              value={settings.host}
              onChange={e => setSettings(s => ({ ...s, host: e.target.value }))}
              placeholder="smtp.example.com"
              className={inputClass}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Port</Label>
            <input
              type="number"
              value={settings.port}
              onChange={e => setSettings(s => ({ ...s, port: parseInt(e.target.value) || 587 }))}
              className={inputClass}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="smtp-secure"
            checked={settings.secure}
            onChange={e => setSettings(s => ({ ...s, secure: e.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 accent-[#212b4f]"
          />
          <Label htmlFor="smtp-secure" className="text-xs text-slate-600 cursor-pointer">
            Use TLS/SSL (enable for port 465)
          </Label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Username</Label>
            <input
              type="text"
              value={settings.username}
              onChange={e => setSettings(s => ({ ...s, username: e.target.value }))}
              placeholder="smtp-username"
              className={inputClass}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Password / API Key</Label>
            <input
              type="password"
              value={settings.password}
              onChange={e => setSettings(s => ({ ...s, password: e.target.value }))}
              placeholder="••••••••"
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4 space-y-4">
          <div className="flex items-center gap-2 pb-1">
            <span className="text-sm font-medium text-slate-700">Sender Details</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">From Email</Label>
              <input
                type="email"
                value={settings.from_email}
                onChange={e => setSettings(s => ({ ...s, from_email: e.target.value }))}
                placeholder="noreply@yourcompany.com"
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">From Name</Label>
              <input
                type="text"
                value={settings.from_name}
                onChange={e => setSettings(s => ({ ...s, from_name: e.target.value }))}
                placeholder="Your Company"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#212b4f] hover:bg-[#2d3a6b] text-white h-9 text-sm gap-1.5"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save SMTP Settings
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
          <Send className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Test Email Delivery</span>
        </div>

        <p className="text-xs text-slate-500">
          Send a test email using the saved SMTP settings. If left blank, the test will be sent to the From Email address.
        </p>

        <div className="flex gap-3 items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs text-slate-600">Send test to</Label>
            <input
              type="email"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder={settings.from_email || 'you@example.com'}
              className={inputClass}
            />
          </div>
          <Button
            onClick={handleTestEmail}
            disabled={testResult.status === 'sending'}
            variant="outline"
            className="h-9 text-sm gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50 shrink-0"
          >
            {testResult.status === 'sending'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Send className="h-3.5 w-3.5" />}
            Send Test
          </Button>
        </div>

        {testResult.status !== 'idle' && (
          <div className={`rounded-lg border text-sm ${
            testResult.status === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : testResult.status === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-slate-50 border-slate-200 text-slate-600'
          }`}>
            <div className="flex items-start gap-2.5 px-3 py-2.5">
              {testResult.status === 'success' && <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-green-600" />}
              {testResult.status === 'error' && <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />}
              {testResult.status === 'sending' && <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium">{testResult.message}</p>
                {testResult.detail && (
                  <button
                    onClick={() => setShowDetail(v => !v)}
                    className="mt-1 flex items-center gap-1 text-xs opacity-70 hover:opacity-100 transition-opacity"
                  >
                    {showDetail ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {showDetail ? 'Hide details' : 'Show details'}
                  </button>
                )}
                {testResult.detail && showDetail && (
                  <pre className="mt-2 text-xs bg-black/5 rounded p-2 whitespace-pre-wrap break-all font-mono leading-relaxed">
                    {testResult.detail}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
